import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/local-seo-guide");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/local-seo-guide")({
  head: () => ({
    meta: [
        { name: "description", content: "The plain-English local SEO guide for Colorado small businesses in 2026. Google Business Profile, citations, reviews, and ranking the map pack." },
      {
        title: "Plain-English Local SEO Guide for Colorado SMBs",
      },
      {
        name: "description",
        content:
          "What local SEO actually is, how it works in 2026, and the 6 things that actually move the needle for Colorado Springs and Denver service businesses.",
      },
      {
        property: "og:title",
        content: "Local SEO for Colorado Small Businesses — Plain-English Guide",
      },
      {
        property: "og:description",
        content:
          "Honest, jargon-free local SEO playbook from a 25-year, in-house Colorado agency.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/local-seo-guide` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/local-seo-guide" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="SEO"
      read="11 min read"
      title="The plain-English guide to local SEO for Colorado small businesses"
      intro="If you&rsquo;ve been told local SEO is &ldquo;too complicated&rdquo; or &ldquo;a long-term play with no proof,&rdquo; you&rsquo;ve been talking to the wrong people. Here&rsquo;s how it really works in Colorado in 2026, in plain English."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Local SEO guide" },
      ]}
      path="/learn/local-seo-guide"
      faqs={[
        { q: "How long does local SEO take to work in Colorado?", a: "Most Colorado Springs and Denver service businesses see Google Business Profile movement in 30–60 days, with map-pack ranking shifts between 90–180 days when on-page, citations, and reviews are handled together." },
        { q: "Do I need a new website to do local SEO?", a: "Not always. If your site loads fast on mobile, has clear service and city pages, and is technically sound, we can rank it. If it's slow, thin, or built on a closed platform we can't edit, a rebuild usually pays for itself within a year." },
        { q: "How much should a small Colorado business spend on local SEO?", a: "For a single-location service business, a real local SEO budget starts around $750–$1,500/month. Anything under $500 is almost always automated software with no human strategy behind it." },
      ]}
    >
      <h2>What &ldquo;local SEO&rdquo; actually means</h2>
      <p>
        Local SEO is the work of getting your business to show up when someone in your service area
        searches Google for what you do — like &ldquo;HVAC repair Colorado Springs&rdquo; or
        &ldquo;family lawyer near me.&rdquo; It&rsquo;s a mix of three things: your{" "}
        <strong>website</strong>, your <strong>Google Business Profile</strong>, and the{" "}
        <strong>signals</strong> Google reads from the rest of the web (reviews, citations,
        backlinks).
      </p>
      <p>
        That&rsquo;s it. Anyone telling you it&rsquo;s 47 layers deep is either confused or trying
        to justify a bigger invoice.
      </p>

      <h2>The two results that matter</h2>
      <p>When someone in Colorado searches Google, they see two main types of local results:</p>
      <ul>
        <li>
          <strong>The Map 3-pack</strong> — three Google Business Profile listings with the map
          above them. This is the prime real estate. We have a whole article on{" "}
          <Link to="/learn/google-business-profile" className="font-bold text-brand-gold-deep underline">
            ranking in the Maps 3-pack
          </Link>
          .
        </li>
        <li>
          <strong>The organic blue links</strong> — your website and your competitors&rsquo;
          websites listed below the map.
        </li>
      </ul>
      <p>
        You want both. The Maps 3-pack drives phone calls. Organic results drive longer research
        visits and people comparing options.
      </p>

      <h2>The 6 things that actually move the needle</h2>
      <ol>
        <li>
          <strong>A real, fast, mobile-first website</strong> — Google ranks sites that load fast,
          look good on a phone, and clearly say what you do and where. Most small-business sites
          fail one of those three. (See our{" "}
          <Link to="/colorado-springs-web-design" className="font-bold text-brand-gold-deep underline">
            Web Design service page
          </Link>{" "}
          for what a real one looks like.)
        </li>
        <li>
          <strong>City and service pages</strong> — A separate page for each combination of service
          + city you serve (e.g. &ldquo;Furnace repair in Monument, CO&rdquo;). Done correctly,
          this is the single biggest organic SEO lever in 2026. Done incorrectly, it gets you a
          spam penalty — read our article on{" "}
          <Link to="/learn/service-area-pages" className="font-bold text-brand-gold-deep underline">
            service area pages done right
          </Link>
          .
        </li>
        <li>
          <strong>A fully optimized Google Business Profile</strong> — Categories, services,
          photos, posts, Q&amp;A. This alone can move you from page 3 to the 3-pack inside 90 days
          if you&rsquo;ve never touched it.
        </li>
        <li>
          <strong>Real Google reviews, gathered consistently</strong> — Volume, recency, and
          response rate all matter. Read{" "}
          <Link to="/learn/get-more-reviews" className="font-bold text-brand-gold-deep underline">
            how to get reviews the right way
          </Link>
          .
        </li>
        <li>
          <strong>Citations and backlinks</strong> — Mentions of your business name, address, and
          phone number on directories (Yelp, BBB, Angi, industry-specific sites) and links from
          local Colorado websites (Chamber, news outlets, partners).
        </li>
        <li>
          <strong>Schema markup &amp; technical hygiene</strong> — Behind-the-scenes code that
          tells Google exactly what your business is, where it operates, what it costs, and what
          customers say. Most cheap sites skip it entirely.
        </li>
      </ol>

      <h2>How long it actually takes in Colorado</h2>
      <p>
        Honest timeline for a Colorado service business starting from zero:
      </p>
      <ul>
        <li><strong>Month 1–2:</strong> Google Business Profile cleanup, basic site fixes, citation cleanup. Maps movement starts.</li>
        <li><strong>Month 3–6:</strong> City + service pages start ranking. Organic traffic begins climbing. First measurable lead lift.</li>
        <li><strong>Month 6–12:</strong> Top-3 Maps placement on most core searches. Organic leads usually 2–4× starting point.</li>
        <li><strong>Year 2+:</strong> Compounding. SEO becomes your cheapest lead source by far.</li>
      </ul>
      <p>
        Anyone promising you &ldquo;page 1 in 30 days&rdquo; is either lying or about to get your
        site penalized. See our{" "}
        <Link to="/learn/agency-questions" className="font-bold text-brand-gold-deep underline">
          12 questions to ask any SEO agency
        </Link>{" "}
        before signing.
      </p>

      <h2>What this should cost in Colorado</h2>
      <p>
        Real local SEO in Colorado in 2026 runs <strong>$750–$2,500/month</strong> for most small
        businesses, depending on how competitive your service area is. Anyone charging $99/month is
        running a software tool on autopilot. Anyone charging $5,000+/month had better be doing
        full content production and digital PR. See our{" "}
        <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">
          SEO service page
        </Link>{" "}
        for what we include at each tier — or see the{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          pricing page
        </Link>{" "}
        for our three packages.
      </p>

      <h2>The bottom line</h2>
      <p>
        Local SEO isn&rsquo;t magic. It&rsquo;s six fundamentals done consistently for 12+ months.
        If your current agency can&rsquo;t explain to you, in plain English, which of those six
        levers they&rsquo;re pulling this month — that&rsquo;s a problem. We do all of it in-house,
        no overseas teams, no white-label subcontractors. Just Chris, the owner, and our Colorado
        team.
      </p>
    </ArticleLayout>
  );
}
